const CLIENT_ERROR_MIN = 400;
const CLIENT_ERROR_MAX = 500;

// Apollo maps a handful of NestJS HttpException statuses to these GraphQL codes;
// other 4xx keep `INTERNAL_SERVER_ERROR` and carry the status numerically
// instead (see @nestjs/apollo `createTransformHttpErrorFn`). `QUERY_TOO_COMPLEX`
// is thrown by our own public-query-security plugin and has no status at all.
const CLIENT_ERROR_CODES: ReadonlySet<string> = new Set([
  "BAD_REQUEST",
  "BAD_USER_INPUT",
  "UNAUTHENTICATED",
  "FORBIDDEN",
  "QUERY_TOO_COMPLEX",
]);

type GraphQLErrorLike = {
  extensions?: {
    code?: unknown;
    status?: unknown;
    originalError?: { statusCode?: unknown } | null;
  };
};

const isClientStatus = (value: unknown): boolean =>
  typeof value === "number" && value >= CLIENT_ERROR_MIN && value < CLIENT_ERROR_MAX;

const isClientCode = (value: unknown): boolean => typeof value === "string" && CLIENT_ERROR_CODES.has(value);

// Every NestJS HttpException reaches Apollo with `originalError.statusCode` set;
// non-predefined statuses (e.g. 404) additionally expose `status`. Predefined
// ones (400/401/403/422) surface only through the mapped `code`.
const getGraphqlErrorStatus = ({ extensions }: GraphQLErrorLike): unknown =>
  extensions?.originalError?.statusCode ?? extensions?.status;

const hasClientErrorSignal = (graphqlError: GraphQLErrorLike): boolean =>
  isClientStatus(getGraphqlErrorStatus(graphqlError)) || isClientCode(graphqlError.extensions?.code);

// A retry can only help transient failures (network blips, 5xx). A 4xx means the
// request is wrong and will fail identically on every attempt, so we fail fast
// instead of paying React Query's exponential backoff (~7s for the default 3
// retries). graphql-request wraps GraphQL errors in a `ClientError`, so the 4xx
// signal can hide in a top-level HTTP status, the response envelope, or the
// per-error `extensions`. We never match on the error message: `ClientError`
// serialises the whole response into it, so any payload containing "not found"
// (returned data, a nested 500 message) would be misread as a client error.
export function isNonRetryableClientError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const candidate = error as Record<string, unknown>;
  if (isClientStatus(candidate.status) || isClientStatus(candidate.statusCode)) return true;

  const response = candidate.response;
  if (!response || typeof response !== "object") return false;
  const envelope = response as Record<string, unknown>;
  if (isClientStatus(envelope.status)) return true;

  const errors = envelope.errors;
  if (!Array.isArray(errors)) return false;
  return errors.some((entry) => typeof entry === "object" && entry !== null && hasClientErrorSignal(entry as GraphQLErrorLike));
}
